import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, CheckCircle, ArrowRight, Users, Bed, Activity,
  FileText, Calendar, Clock, Shield, Database, BarChart3,
  Stethoscope, Heart, TrendingUp, Award, Phone, Mail
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const HospitalPartnerships = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const features = [
    {
      icon: Users,
      title: 'Enterprise Patient Management',
      description: 'Unified system for OPD, IPD, and emergency patient management with real-time updates',
      color: '#1D57D5'
    },
    {
      icon: Bed,
      title: 'Bed & Resource Allocation',
      description: 'Smart bed management with automated allocation, tracking, and optimization',
      color: '#28C08A'
    },
    {
      icon: Activity,
      title: 'Multi-Specialty Coordination',
      description: 'Seamless collaboration between departments with shared patient records',
      color: '#F97316'
    },
    {
      icon: Database,
      title: 'Centralized Medical Records',
      description: 'Digital health records with imaging, lab results, and complete patient history',
      color: '#9333EA'
    },
    {
      icon: Calendar,
      title: 'Advanced Scheduling',
      description: 'Automated appointment management for multiple specialties and facilities',
      color: '#E91E8C'
    },
    {
      icon: BarChart3,
      title: 'Analytics & Compliance',
      description: 'Real-time dashboards for operations, quality metrics, and regulatory reporting',
      color: '#F4CE7B'
    }
  ];

  const benefits = [
    'Reduce administrative costs by 35-45%',
    'Improve patient wait times by 60%',
    'Increase bed turnover rate by 25%',
    'Enhance staff productivity by 40%',
    'Achieve 99.9% data accuracy',
    'Full NABH & JCI compliance support'
  ];

  const caseStudies = [
    {
      name: 'Apollo Multispecialty Hospital',
      location: 'Bangalore',
      beds: '250 beds',
      result: '40% reduction in admin costs, 99.8% patient satisfaction',
      metric: '40%',
      metricLabel: 'Cost Reduction'
    },
    {
      name: 'Fortis Healthcare Center',
      location: 'Delhi NCR',
      beds: '180 beds',
      result: '25% increase in bed utilization, seamless multi-specialty coordination',
      metric: '25%',
      metricLabel: 'Bed Utilization'
    },
    {
      name: 'Manipal Hospital Group',
      location: 'Mumbai',
      beds: '400 beds',
      result: 'Full digital transformation in 3 months, 50% faster discharge process',
      metric: '50%',
      metricLabel: 'Faster Discharge'
    }
  ];

  const implementationSteps = [
    {
      step: '01',
      title: 'Discovery & Planning',
      description: 'Comprehensive assessment of your hospital infrastructure, workflows, and requirements',
      duration: '2 weeks'
    },
    {
      step: '02',
      title: 'Customization & Setup',
      description: 'Platform configuration tailored to your specialties, departments, and processes',
      duration: '4 weeks'
    },
    {
      step: '03',
      title: 'Staff Training',
      description: 'On-site training for doctors, nurses, and administrative staff with ongoing support',
      duration: '2 weeks'
    },
    {
      step: '04',
      title: 'Go-Live & Support',
      description: 'Phased rollout with 24/7 technical support and continuous optimization',
      duration: 'Ongoing'
    }
  ];

  return (
    <div style={{ background: 'white', minHeight: '100vh' }}>
      <Navbar />

      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #0D2C6E 0%, #1D57D5 50%, #28C08A 100%)',
        paddingTop: isMobile ? '90px' : '120px',
        paddingBottom: isMobile ? '60px' : '100px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: isMobile ? '250px' : '400px',
          height: isMobile ? '250px' : '400px',
          background: 'radial-gradient(circle, rgba(244, 206, 123, 0.2) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(60px)'
        }} />

        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 32px', position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(244, 206, 123, 0.15)',
            backdropFilter: 'blur(10px)',
            border: '2px solid rgba(244, 206, 123, 0.3)',
            borderRadius: '50px',
            padding: '10px 24px',
            marginBottom: '32px'
          }}>
            <Building2 size={20} color="#F4CE7B" />
            <span style={{ color: '#F4CE7B', fontSize: '14px', fontWeight: '700', letterSpacing: '0.5px' }}>
              FOR HOSPITALS & HEALTH SYSTEMS
            </span>
          </div>

          <h1 style={{
            fontSize: isMobile ? '36px' : '64px',
            fontWeight: '900',
            color: 'white',
            lineHeight: '1.1',
            marginBottom: '24px',
            letterSpacing: isMobile ? '-1px' : '-2px'
          }}>
            Transform Your Hospital<br />
            <span style={{
              background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              With Digital Infrastructure
            </span>
          </h1>

          <p style={{
            fontSize: isMobile ? '18px' : '20px',
            color: 'rgba(255, 255, 255, 0.9)',
            lineHeight: '1.6',
            maxWidth: '650px',
            marginBottom: '40px'
          }}>
            Enterprise-grade healthcare platform for multi-specialty hospitals. Streamline operations, improve patient care, and achieve regulatory compliance.
          </p>

          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <button
              onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
              style={{
                background: 'linear-gradient(135deg, #F4CE7B 0%, #E8C068 100%)',
                color: '#0D2C6E',
                border: 'none',
                borderRadius: '50px',
                padding: isMobile ? '16px 32px' : '18px 40px',
                fontSize: isMobile ? '16px' : '18px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                boxShadow: '0 8px 24px rgba(244, 206, 123, 0.4)',
                transition: 'all 0.3s ease',
                width: isMobile ? '100%' : 'auto',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(244, 206, 123, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(244, 206, 123, 0.4)';
              }}
            >
              Request Demo
              <ArrowRight size={20} />
            </button>
            <button
              onClick={() => document.getElementById('case-studies').scrollIntoView({ behavior: 'smooth' })}
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '50px',
                padding: isMobile ? '16px 32px' : '18px 40px',
                fontSize: isMobile ? '16px' : '18px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                width: isMobile ? '100%' : 'auto',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.border = '2px solid rgba(255, 255, 255, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.border = '2px solid rgba(255, 255, 255, 0.3)';
              }}
            >
              View Case Studies
            </button>
          </div>

          {/* Trust Indicators */}
          <div style={{
            display: 'flex',
            gap: '48px',
            marginTop: '48px',
            flexWrap: 'wrap'
          }}>
            <div>
              <div style={{ fontSize: '36px', fontWeight: '900', color: '#F4CE7B' }}>150+</div>
              <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)' }}>Partner Hospitals</div>
            </div>
            <div>
              <div style={{ fontSize: '36px', fontWeight: '900', color: '#F4CE7B' }}>50K+</div>
              <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)' }}>Beds Managed</div>
            </div>
            <div>
              <div style={{ fontSize: '36px', fontWeight: '900', color: '#F4CE7B' }}>2M+</div>
              <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)' }}>Patients Served</div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div style={{
        background: 'linear-gradient(180deg, #F8FAFC 0%, white 100%)',
        padding: isMobile ? '60px 0' : '100px 0'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 32px' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 style={{
              fontSize: '48px',
              fontWeight: '900',
              color: '#1A202C',
              marginBottom: '16px',
              letterSpacing: '-1px'
            }}>
              Comprehensive Hospital Management
            </h2>
            <p style={{
              fontSize: '20px',
              color: '#64748B',
              maxWidth: '700px',
              margin: '0 auto'
            }}>
              Everything you need to run a modern, efficient healthcare facility
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px'
          }}>
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div
                  key={index}
                  style={{
                    background: 'white',
                    border: '2px solid #E8ECF0',
                    borderRadius: '20px',
                    padding: '40px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.12)';
                    e.currentTarget.style.borderColor = feature.color;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#E8ECF0';
                  }}
                >
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '72px',
                    height: '72px',
                    background: `${feature.color}15`,
                    borderRadius: '16px',
                    marginBottom: '24px'
                  }}>
                    <IconComponent size={36} color={feature.color} />
                  </div>
                  <h3 style={{
                    fontSize: '22px',
                    fontWeight: '700',
                    color: '#1A202C',
                    marginBottom: '12px'
                  }}>
                    {feature.title}
                  </h3>
                  <p style={{
                    fontSize: '16px',
                    color: '#64748B',
                    lineHeight: '1.7'
                  }}>
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div style={{ background: '#F8FAFC', padding: isMobile ? '60px 0' : '50px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 32px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '48px' : '80px', alignItems: 'center' }}>
            <div>
              <div style={{
                display: 'inline-block',
                padding: '6px 16px',
                background: '#E0F2FE',
                color: '#0284C7',
                borderRadius: '50px',
                fontSize: '14px',
                fontWeight: '700',
                marginBottom: '24px',
                letterSpacing: '0.5px'
              }}>
                PROVEN RESULTS
              </div>
              <h2 style={{
                fontSize: '48px',
                fontWeight: '900',
                color: '#0F172A',
                marginBottom: '24px',
                letterSpacing: '-1px',
                lineHeight: '1.2'
              }}>
                Measurable<br />
                <span style={{
                  background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>Business Impact</span>
              </h2>
              <p style={{
                fontSize: '18px',
                color: '#475569',
                lineHeight: '1.7',
                marginBottom: '40px',
                maxWidth: '540px'
              }}>
                GEWO's hospital management platform delivers tangible results across operations, finance, and patient care quality. Use real-time data to drive decisions.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {benefits.map((benefit, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    background: 'white',
                    padding: '16px 20px',
                    borderRadius: '16px',
                    boxShadow: '0 4px 12px rgba(148, 163, 184, 0.05)',
                    border: '1px solid #F1F5F9'
                  }}>
                    <div style={{
                      minWidth: '32px',
                      minHeight: '32px',
                      background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 10px rgba(16, 185, 129, 0.2)'
                    }}>
                      <CheckCircle size={18} color="white" />
                    </div>
                    <span style={{ fontSize: '16px', color: '#334155', fontWeight: '600' }}>
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Visual Stats */}
            <div style={{
              background: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
              borderRadius: '32px',
              padding: isMobile ? '32px' : '48px',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.25)'
            }}>
              {/* Background Shapes */}
              <div style={{
                position: 'absolute',
                top: '-20%',
                right: '-20%',
                width: '300px',
                height: '300px',
                background: 'rgba(37, 99, 235, 0.15)',
                filter: 'blur(80px)',
                borderRadius: '50%'
              }} />
              <div style={{
                position: 'absolute',
                bottom: '-10%',
                left: '-10%',
                width: '250px',
                height: '250px',
                background: 'rgba(16, 185, 129, 0.1)',
                filter: 'blur(60px)',
                borderRadius: '50%'
              }} />

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '24px',
                position: 'relative',
                zIndex: 1
              }}>
                {[
                  { value: '35%', label: 'Cost Reduction', trend: '+12%', icon: TrendingUp, color: '#F472B6' },
                  { value: '60%', label: 'Faster Wait Times', trend: '-25 min', icon: Clock, color: '#34D399' },
                  { value: '25%', label: 'Higher Bed Util.', trend: '+8 avail.', icon: Bed, color: '#60A5FA' },
                  { value: '40%', label: 'Staff Productivity', trend: '+20 pts', icon: Users, color: '#FBBF24' }
                ].map((stat, index) => {
                  const StatIcon = stat.icon;
                  return (
                    <div key={index} style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '24px',
                      padding: '24px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      transition: 'transform 0.3s ease',
                      cursor: 'default'
                    }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '16px'
                      }}>
                        <div style={{
                          padding: '10px',
                          background: 'rgba(255, 255, 255, 0.1)',
                          borderRadius: '14px'
                        }}>
                          <StatIcon size={20} color={stat.color} />
                        </div>
                        <div style={{
                          fontSize: '12px',
                          fontWeight: '600',
                          color: stat.color,
                          background: 'rgba(255, 255, 255, 0.05)',
                          padding: '4px 8px',
                          borderRadius: '8px'
                        }}>
                          {stat.trend}
                        </div>
                      </div>
                      <div style={{ fontSize: '36px', fontWeight: '800', color: 'white', marginBottom: '4px' }}>
                        {stat.value}
                      </div>
                      <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)', fontWeight: '500' }}>
                        {stat.label}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Case Studies */}
      <div id="case-studies" style={{
        background: 'linear-gradient(180deg, #F8FAFC 0%, white 100%)',
        padding: isMobile ? '60px 0' : '50px 0'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 32px' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 style={{
              fontSize: '48px',
              fontWeight: '900',
              color: '#1A202C',
              marginBottom: '16px',
              letterSpacing: '-1px'
            }}>
              Trusted by Leading Hospitals
            </h2>
            <p style={{
              fontSize: '20px',
              color: '#64748B',
              maxWidth: '700px',
              margin: '0 auto'
            }}>
              Real results from hospitals transforming their operations with GEWO
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px'
          }}>
            {caseStudies.map((study, index) => (
              <div
                key={index}
                style={{
                  background: 'white',
                  border: '2px solid #E8ECF0',
                  borderRadius: '20px',
                  padding: '36px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '64px',
                  height: '64px',
                  background: 'linear-gradient(135deg, #1D57D5 0%, #286CFF 100%)',
                  borderRadius: '16px',
                  marginBottom: '20px'
                }}>
                  <Building2 size={32} color="white" />
                </div>
                <h3 style={{
                  fontSize: '22px',
                  fontWeight: '700',
                  color: '#1A202C',
                  marginBottom: '8px'
                }}>
                  {study.name}
                </h3>
                <div style={{
                  fontSize: '14px',
                  color: '#64748B',
                  marginBottom: '4px'
                }}>
                  {study.location} • {study.beds}
                </div>
                <div style={{
                  fontSize: '40px',
                  fontWeight: '900',
                  color: '#28C08A',
                  marginTop: '20px',
                  marginBottom: '8px'
                }}>
                  {study.metric}
                </div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#28C08A',
                  marginBottom: '16px'
                }}>
                  {study.metricLabel}
                </div>
                <p style={{
                  fontSize: '15px',
                  color: '#4A4A4A',
                  lineHeight: '1.6'
                }}>
                  {study.result}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Implementation Process */}
      <div style={{ background: 'white', padding: isMobile ? '60px 0' : '50px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 32px' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <h2 style={{
              fontSize: '48px',
              fontWeight: '900',
              color: '#1A202C',
              marginBottom: '16px',
              letterSpacing: '-1px'
            }}>
              Implementation Process
            </h2>
            <p style={{
              fontSize: '20px',
              color: '#64748B',
              maxWidth: '700px',
              margin: '0 auto'
            }}>
              From planning to go-live in as little as 8 weeks
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '32px'
          }}>
            {implementationSteps.map((step, index) => (
              <div key={index} style={{ textAlign: 'center', position: 'relative' }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '80px',
                  height: '80px',
                  background: 'linear-gradient(135deg, #1D57D5 0%, #286CFF 100%)',
                  borderRadius: '50%',
                  marginBottom: '24px',
                  boxShadow: '0 8px 24px rgba(29, 87, 213, 0.3)'
                }}>
                  <span style={{ fontSize: '28px', fontWeight: '900', color: 'white' }}>
                    {step.step}
                  </span>
                </div>
                <h4 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#1A202C',
                  marginBottom: '12px'
                }}>
                  {step.title}
                </h4>
                <p style={{
                  fontSize: '15px',
                  color: '#64748B',
                  lineHeight: '1.6',
                  marginBottom: '12px'
                }}>
                  {step.description}
                </p>
                <div style={{
                  display: 'inline-block',
                  background: 'linear-gradient(135deg, #F4CE7B 0%, #E8C068 100%)',
                  color: '#0D2C6E',
                  borderRadius: '20px',
                  padding: '6px 16px',
                  fontSize: '13px',
                  fontWeight: '700'
                }}>
                  {step.duration}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contact CTA */}
      <div id="contact-form" style={{
        background: 'linear-gradient(135deg, #0D2C6E 0%, #1D57D5 50%, #286CFF 100%)',
        padding: isMobile ? '60px 0' : '100px 0'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          padding: '0 32px',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontSize: isMobile ? '36px' : '56px',
            fontWeight: '900',
            color: 'white',
            lineHeight: '1.2',
            marginBottom: '24px',
            letterSpacing: '-1px'
          }}>
            Ready to Transform<br />
            <span style={{
              background: 'linear-gradient(135deg, #F4CE7B 0%, #FFD98E 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Your Hospital Operations?
            </span>
          </h2>
          <p style={{
            fontSize: '20px',
            color: 'rgba(255, 255, 255, 0.9)',
            marginBottom: '48px',
            lineHeight: '1.6'
          }}>
            Schedule a personalized demo to see how GEWO can revolutionize your healthcare facility.
          </p>

          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => window.open('mailto:hospitals@gewohealth.com', '_blank')}
              style={{
                background: 'linear-gradient(135deg, #F4CE7B 0%, #E8C068 100%)',
                color: '#0D2C6E',
                border: 'none',
                borderRadius: '50px',
                padding: isMobile ? '16px 32px' : '20px 48px',
                fontSize: isMobile ? '16px' : '18px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                boxShadow: '0 8px 24px rgba(244, 206, 123, 0.4)',
                transition: 'all 0.3s ease',
                width: isMobile ? '100%' : 'auto',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(244, 206, 123, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(244, 206, 123, 0.4)';
              }}
            >
              Request Demo
              <ArrowRight size={20} />
            </button>
            <button
              onClick={() => navigate('/partners')}
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '50px',
                padding: isMobile ? '16px 32px' : '20px 48px',
                fontSize: isMobile ? '16px' : '18px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                width: isMobile ? '100%' : 'auto',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.border = '2px solid rgba(255, 255, 255, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.border = '2px solid rgba(255, 255, 255, 0.3)';
              }}
            >
              View All Partnerships
            </button>
          </div>

          <div style={{
            marginTop: '48px',
            display: 'flex',
            gap: '32px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Phone size={18} color="#F4CE7B" />
              <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '16px' }}>
                +91 99999 00000
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Mail size={18} color="#F4CE7B" />
              <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '16px' }}>
                hospitals@gewohealth.com
              </span>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HospitalPartnerships;